import {
	IArrow,
	ICoordinate,
	ICrossingLine,
	IDimensions,
	IFormattedCrossingLinesResult,
	LineDetailsType
} from "./interfaces";

export const getPointsDirection = (point1: ICoordinate, point2: ICoordinate, point3: ICoordinate): number => {
	const value = (point2.y - point1.y) * (point3.x - point2.x) - (point2.x - point1.x) * (point3.y - point2.y);

	if (value === 0) return 0;
	if (value > 0) return 1;
	return 2;
};

export const checkPointOnLine = (line: IArrow, point: ICoordinate): boolean => {
	if (!line.start || !line.end) return false;

	const condition1 = Math.min(line.start.x, line.end.x) <= point.x;
	const condition2 = point.x <= Math.max(line.start.x, line.end.x);

	const condition3 = Math.min(line.start.y, line.end.y) <= point.y;
	const condition4 = point.y <= Math.max(line.start.y, line.end.y);

	if (condition1 && condition2 && condition3 && condition4) {
		return true;
	}

	return false;
};

export const checkIntersection = (line1: IArrow, line2: IArrow): boolean => {
	if (!line1.start || !line1.end || !line2.start || !line2.end) return false;

	const direction1 = getPointsDirection(line1.start, line1.end, line2.start);
	const direction2 = getPointsDirection(line1.start, line1.end, line2.end);
	const direction3 = getPointsDirection(line2.start, line2.end, line1.start);
	const direction4 = getPointsDirection(line2.start, line2.end, line1.end);

	if (direction1 !== direction2 && direction3 !== direction4) return true; //the lines are intersecting

	if (direction1 === 0 && checkPointOnLine(line1, line2.start))
		//when p2 of line2 is on the line1
		return true;

	if (direction2 === 0 && checkPointOnLine(line1, line2.end))
		//when p1 of line2 is on the line1
		return true;

	if (direction3 === 0 && checkPointOnLine(line2, line1.start))
		//when p2 of line1 is on the line2
		return true;

	if (direction4 === 0 && checkPointOnLine(line2, line1.end))
		//when p1 of line1 is on the line2
		return true;

	return false;
};

export const checkLineIntersectsROIPolygon = (line: IArrow, polygonCoordinates: ICoordinate[]): boolean => {
	for (let index = 0; index < polygonCoordinates.length; index++) {
		const currentCoordinateItem = polygonCoordinates[index];
		const nextCoordinateItem =
			index === polygonCoordinates.length - 1 ? polygonCoordinates[0] : polygonCoordinates[index + 1];

		const isIntersecting = checkIntersection({ start: currentCoordinateItem, end: nextCoordinateItem }, line);

		if (isIntersecting) return true;
	}

	return false;
};

export const checkPointInsideROIPolygon = (point: ICoordinate, polygonCoordinates: ICoordinate[]): boolean => {
	const line = {
		start: point,
		end: {
			x: 999999,
			y: point.y
		}
	};

	let count = 0;

	for (let index = 0; index < polygonCoordinates.length; index++) {
		const currentCoordinateItem = polygonCoordinates[index];
		const nextCoordinateItem =
			index === polygonCoordinates.length - 1 ? polygonCoordinates[0] : polygonCoordinates[index + 1];

		const isIntersecting = checkIntersection({ start: currentCoordinateItem, end: nextCoordinateItem }, line);

		if (isIntersecting) {
			const pointsDirection = getPointsDirection(currentCoordinateItem, point, nextCoordinateItem);
			if (pointsDirection === 0) return true;

			count = count + 1;
		}
	}

	return count % 2 !== 0;
};

export const getCrossingLineDirectionalArrow = (startCoordinate: ICoordinate, endCoordinate: ICoordinate): IArrow => {
	const centerPoint = {
		x: (startCoordinate.x + endCoordinate.x) / 2,
		y: (startCoordinate.y + endCoordinate.y) / 2
	};

	const lineDistance = {
		x: endCoordinate.x - startCoordinate.x,
		y: endCoordinate.y - startCoordinate.y
	};
	const lineAngle = Math.atan2(lineDistance.y, lineDistance.x);

	let directionCoordinate = {
		x: centerPoint.x - 15 * Math.cos(lineAngle - Math.PI / 2),
		y: centerPoint.y - 15 * Math.sin(lineAngle - Math.PI / 2)
	};

	return {
		start: centerPoint,
		end: directionCoordinate
	};
};

export const getScaledCoordinate = (
	coordinate: ICoordinate,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions,
	getCanvasCoordinate = false
): ICoordinate => {
	const { height: imageHeight, width: imageWidth } = imageDimensions;
	const { height: canvasHeight, width: canvasWidth } = canvasDimensions;

	if (getCanvasCoordinate) {
		return {
			x: (canvasWidth * coordinate.x) / imageWidth,
			y: (canvasHeight * coordinate.y) / imageHeight
		};
	}

	return {
		x: (imageWidth * coordinate.x) / canvasWidth,
		y: (imageHeight * coordinate.y) / canvasHeight
	};
};

export const getFormattedROIPolygonString = (
	roiPolygonCoordinates: ICoordinate[],
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions,
	preview = false
): string => {
	let formattedROIString = "";

	if (preview) {
		for (const coordinateItem of roiPolygonCoordinates) {
			if (formattedROIString) {
				formattedROIString = `${formattedROIString},`;
			}

			const scaledCoordinate = getScaledCoordinate(coordinateItem, imageDimensions, canvasDimensions);

			// formattedROIString = `${formattedROIString}${Math.round(scaledCoordinate.x)},${Math.round(scaledCoordinate.y)}`;
			formattedROIString = `${formattedROIString}${scaledCoordinate.x},${scaledCoordinate.y}`;
		}

		return formattedROIString;
	}

	/**
	 * ROI POLYGON COORDINATES - X & Y SEPARATED BY ';'
	 */
	for (const coordinateItem of roiPolygonCoordinates) {
		const scaledCoordinate = getScaledCoordinate(coordinateItem, imageDimensions, canvasDimensions);

		// formattedROIString = `${formattedROIString}${Math.round(scaledCoordinate.x)};${Math.round(scaledCoordinate.y)};`;
		formattedROIString = `${formattedROIString}${scaledCoordinate.x};${scaledCoordinate.y};`;
	}

	return formattedROIString;
};

export const getFormattedLineString = (
	directionalArrowCoordinates: IArrow,
	crossingLinesCoordinates: ICrossingLine[],
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): string => {
	if (!directionalArrowCoordinates.start || !directionalArrowCoordinates.end) return "";

	let formattedLineString = "";

	const scaledDirectionArrowStartCoordinate = getScaledCoordinate(
		directionalArrowCoordinates.start,
		imageDimensions,
		canvasDimensions
	);
	const scaledDirectionArrowEndCoordinate = getScaledCoordinate(
		directionalArrowCoordinates.end,
		imageDimensions,
		canvasDimensions
	);

	/**
	 * DA => DIRECTIONAL ARROW
	 * CL => CROSSING LINE
	 * [DA > start > x];[DA > start > y];[DA > end > x];[DA > end > y];[CL > start > x];[CL > start > y];[CL > end > x];[CL > end > y];
	 * SEPARATED BY '|'
	 */
	for (const crossingLineItem of crossingLinesCoordinates) {
		if (formattedLineString) {
			formattedLineString = `${formattedLineString}|`;
		}

		if (!crossingLineItem.start || !crossingLineItem.end) return "";

		const scaledStartCoordinate = getScaledCoordinate(crossingLineItem.start, imageDimensions, canvasDimensions);
		const scaledEndCoordinate = getScaledCoordinate(crossingLineItem.end, imageDimensions, canvasDimensions);

		const scaledDirectionArrowCoordinate = {
			start: scaledDirectionArrowStartCoordinate,
			end: scaledDirectionArrowEndCoordinate
		};

		if (crossingLineItem.isFinalLine) {
			formattedLineString = `${formattedLineString}$`;
		}

		// DIRECTION ARROW START COORDINATE
		// formattedLineString = `${formattedLineString}${Math.round(scaledDirectionArrowCoordinate.start.x)};${Math.round(
		// 	scaledDirectionArrowCoordinate.start.y
		// )};`;
		formattedLineString = `${formattedLineString}${scaledDirectionArrowCoordinate.start.x};${scaledDirectionArrowCoordinate.start.y};`;

		// DIRECTION ARROW END COORDINATE
		// formattedLineString = `${formattedLineString}${Math.round(scaledDirectionArrowCoordinate.end.x)};${Math.round(
		// 	scaledDirectionArrowCoordinate.end.y
		// )};`;
		formattedLineString = `${formattedLineString}${scaledDirectionArrowCoordinate.end.x};${scaledDirectionArrowCoordinate.end.y};`;

		// CROSSING LINE START COORDINATE
		// formattedLineString = `${formattedLineString}${Math.round(scaledStartCoordinate.x)};${Math.round(
		// 	scaledStartCoordinate.y
		// )};`;
		formattedLineString = `${formattedLineString}${scaledStartCoordinate.x};${scaledStartCoordinate.y};`;

		// CROSSING LINE END COORDINATE
		// formattedLineString = `${formattedLineString}${Math.round(scaledEndCoordinate.x)};${Math.round(
		// 	scaledEndCoordinate.y
		// )};`;
		formattedLineString = `${formattedLineString}${scaledEndCoordinate.x};${scaledEndCoordinate.y};`;
	}

	return formattedLineString;
};

export const getFormattedDirectionalArrowString = (
	directionalArrowCoordinates: IArrow,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): string => {
	if (!directionalArrowCoordinates.start || !directionalArrowCoordinates.end) return "";

	let formattedDirectionArrowCoordinates = "";

	// START COORDINATE
	const scaledStartCoordinate = getScaledCoordinate(
		directionalArrowCoordinates.start,
		imageDimensions,
		canvasDimensions
	);

	// formattedDirectionArrowCoordinates = `${Math.round(scaledStartCoordinate.x)},${Math.round(scaledStartCoordinate.y)}`;
	formattedDirectionArrowCoordinates = `${scaledStartCoordinate.x},${scaledStartCoordinate.y}`;

	// END COORDINATE
	const scaledEndCoordinate = getScaledCoordinate(directionalArrowCoordinates.end, imageDimensions, canvasDimensions);

	// formattedDirectionArrowCoordinates = `${formattedDirectionArrowCoordinates},${Math.round(
	// 	scaledEndCoordinate.x
	// )},${Math.round(scaledEndCoordinate.y)}`;
	formattedDirectionArrowCoordinates = `${formattedDirectionArrowCoordinates},${scaledEndCoordinate.x},${scaledEndCoordinate.y}`;

	return formattedDirectionArrowCoordinates;
};

export const getFormattedCrossingLines = (
	crossingLinesCoordinates: ICrossingLine[],
	ROIPolygonCoordinates: ICoordinate[],
	canvasChanged: boolean,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): IFormattedCrossingLinesResult => {
	let formattedCrossingLinesCoordinates = [];

	for (const crossingLineItem of crossingLinesCoordinates) {
		if (!crossingLineItem.start || !crossingLineItem.end) {
			return {
				success: false,
				result: [],
				message: "Please draw crossing lines"
			};
		}

		const lineIntersectsPolygon = checkLineIntersectsROIPolygon(
			{
				start: crossingLineItem.start,
				end: crossingLineItem.end
			},
			ROIPolygonCoordinates
		);

		if (lineIntersectsPolygon && canvasChanged) {
			return {
				success: false,
				result: [],
				message: "The crossing lines should be inside ROI"
			};
		}

		const lineInsidePolygon = checkPointInsideROIPolygon(crossingLineItem.start, ROIPolygonCoordinates);

		if (!lineInsidePolygon && canvasChanged) {
			return {
				success: false,
				result: [],
				message: "The crossing lines should be inside ROI"
			};
		}

		let formattedCrossingLineCoordinateString = "";

		const scaledStartCoordinate = getScaledCoordinate(crossingLineItem.start, imageDimensions, canvasDimensions);
		formattedCrossingLineCoordinateString = `${scaledStartCoordinate.x},${scaledStartCoordinate.y}`;

		const scaledEndCoordinate = getScaledCoordinate(crossingLineItem.end, imageDimensions, canvasDimensions);
		formattedCrossingLineCoordinateString = `${formattedCrossingLineCoordinateString},${scaledEndCoordinate.x},${scaledEndCoordinate.y}`;

		if (
			!crossingLineItem.directionLineCoordinates ||
			!crossingLineItem.directionLineCoordinates.start ||
			!crossingLineItem.directionLineCoordinates.end
		) {
			return {
				success: false,
				result: [],
				message: "Could not generate Crossing Lines"
			};
		}

		const scaledDirectionArrowStartCoordinate = getScaledCoordinate(
			crossingLineItem.directionLineCoordinates.start,
			imageDimensions,
			canvasDimensions
		);
		formattedCrossingLineCoordinateString = `${formattedCrossingLineCoordinateString},${scaledDirectionArrowStartCoordinate.x},${scaledDirectionArrowStartCoordinate.y}`;

		const scaledDirectionArrowEndCoordinate = getScaledCoordinate(
			crossingLineItem.directionLineCoordinates.end,
			imageDimensions,
			canvasDimensions
		);
		formattedCrossingLineCoordinateString = `${formattedCrossingLineCoordinateString},${scaledDirectionArrowEndCoordinate.x},${scaledDirectionArrowEndCoordinate.y}`;

		formattedCrossingLinesCoordinates.push(formattedCrossingLineCoordinateString);
	}

	return {
		success: true,
		result: formattedCrossingLinesCoordinates,
		message: ""
	};
};

export const getPreviewROIPolygonDetails = (
	ROIToRender: string,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): ICoordinate[] => {
	const previewROICoordinates = [];

	const previewROICoordinatesArray = ROIToRender.split(",");

	for (let index = 0; index < previewROICoordinatesArray.length; index += 2) {
		const coordinate = {
			x: Number(previewROICoordinatesArray[index]),
			y: Number(previewROICoordinatesArray[index + 1])
		};

		const scaledCoordinate = getScaledCoordinate(coordinate, imageDimensions, canvasDimensions, true);

		previewROICoordinates.push(scaledCoordinate);
	}

	return previewROICoordinates;
};

export const getPreviewDirectionalArrowDetails = (
	directionArrowToRender: string,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): IArrow => {
	const previewDirectionalArrowCoordinates: IArrow = {
		start: null,
		end: null
	};

	const previewDirectionalArrowCoordinatesArray = directionArrowToRender.split(",");

	if (previewDirectionalArrowCoordinatesArray.length === 4) {
		const startCoordinate = {
			x: Number(previewDirectionalArrowCoordinatesArray[0]),
			y: Number(previewDirectionalArrowCoordinatesArray[1])
		};
		const endCoordinate = {
			x: Number(previewDirectionalArrowCoordinatesArray[2]),
			y: Number(previewDirectionalArrowCoordinatesArray[3])
		};

		const scaledStartCoordinate = getScaledCoordinate(startCoordinate, imageDimensions, canvasDimensions, true);
		const scaledEndCoordinate = getScaledCoordinate(endCoordinate, imageDimensions, canvasDimensions, true);

		previewDirectionalArrowCoordinates.start = scaledStartCoordinate;
		previewDirectionalArrowCoordinates.end = scaledEndCoordinate;
	}

	return previewDirectionalArrowCoordinates;
};

export const getPreviewCrossingLinesDetails = (
	crossingLinesToRender: LineDetailsType,
	finalLines: LineDetailsType,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): ICrossingLine[] => {
	const finalLinesDetails = [];

	for (const finalLineItem of finalLines) {
		const startElement = finalLineItem[0];
		const endElement = finalLineItem[1];

		finalLinesDetails.push({
			start: {
				x: startElement[0],
				y: startElement[1]
			},
			end: {
				x: endElement[0],
				y: endElement[1]
			}
		});
	}

	const previewCrossingLinesCoordinates = [];

	for (const crossingLineItem of crossingLinesToRender) {
		const startCoordinateElement = crossingLineItem[0];
		const endCoordinateElement = crossingLineItem[1];

		if (!startCoordinateElement || !endCoordinateElement) continue;

		const startCoordinate: ICoordinate = {
			x: startCoordinateElement[0],
			y: startCoordinateElement[1]
		};
		const endCoordinate = {
			x: endCoordinateElement[0],
			y: endCoordinateElement[1]
		};

		const crossingLineCoordinate = {
			start: startCoordinate,
			end: endCoordinate
		};

		const finalLineElement = finalLinesDetails.find(
			(item) => JSON.stringify(item) === JSON.stringify(crossingLineCoordinate)
		);

		const scaledStartCoordinate = getScaledCoordinate(startCoordinate, imageDimensions, canvasDimensions, true);
		const scaledEndCoordinate = getScaledCoordinate(endCoordinate, imageDimensions, canvasDimensions, true);

		const crossingLineDirectionArrowCoordinates = getCrossingLineDirectionalArrow(
			scaledStartCoordinate,
			scaledEndCoordinate
		);

		previewCrossingLinesCoordinates.push({
			start: scaledStartCoordinate,
			end: scaledEndCoordinate,
			directionLineCoordinates: crossingLineDirectionArrowCoordinates,
			isFinalLine: !!finalLineElement
		});
	}

	return previewCrossingLinesCoordinates;
};

export const getROIPolygonDetailsFromString = (
	roiString: string,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): ICoordinate[] => {
	const ROIArray = [];

	const previewROICoordinatesArray = roiString.split(";");

	if (previewROICoordinatesArray.length > 0) {
		for (let index = 0; index < previewROICoordinatesArray.length; index += 2) {
			const currentCoordinateItem = Number(previewROICoordinatesArray[index]);
			const nextCoordinateItem =
				index === previewROICoordinatesArray.length - 1
					? Number(previewROICoordinatesArray[0])
					: Number(previewROICoordinatesArray[index + 1]);

			if (currentCoordinateItem && nextCoordinateItem) {
				const coordinate: ICoordinate = {
					x: currentCoordinateItem,
					y: nextCoordinateItem
				};

				ROIArray.push(getScaledCoordinate(coordinate, imageDimensions, canvasDimensions, true));
			}
		}
	}

	return ROIArray;
};

export const getLinesDetailsFromString = (
	linesString: string,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions
): { crossingLinesArray: ICrossingLine[]; directionalLine: IArrow } => {
	const crossingLinesArray = [];
	const directionalLine: IArrow = {
		start: null,
		end: null
	};

	const previewCrossingLinesCoordinatesArray = linesString.split("|");

	if (previewCrossingLinesCoordinatesArray.length > 0) {
		for (const previewCrossingLineItem of previewCrossingLinesCoordinatesArray) {
			const previewLinesCoordinatesArray = previewCrossingLineItem.split(";");

			const directionLineCoordinatesArray = previewLinesCoordinatesArray.slice(
				0,
				previewLinesCoordinatesArray.length / 2
			);
			const lineCoordinatesArray = previewLinesCoordinatesArray.slice(previewLinesCoordinatesArray.length / 2);

			const lineItem: ICrossingLine = {
				start: null,
				end: null,
				directionLineCoordinates: {
					start: null,
					end: null
				}
			};

			if (previewCrossingLineItem.startsWith("$")) {
				lineItem.isFinalLine = true;
			}

			if (directionLineCoordinatesArray.length >= 4 && lineCoordinatesArray.length >= 4) {
				const start: ICoordinate = {
					x: Number(lineCoordinatesArray[0]),
					y: Number(lineCoordinatesArray[1])
				};
				const end: ICoordinate = {
					x: Number(lineCoordinatesArray[2]),
					y: Number(lineCoordinatesArray[3])
				};

				if (!directionalLine.start && !directionalLine.end) {
					const directionLineStart: ICoordinate = {
						x: Number(directionLineCoordinatesArray[0].replace("$", "")),
						y: Number(directionLineCoordinatesArray[1])
					};

					const directionLineEnd: ICoordinate = {
						x: Number(directionLineCoordinatesArray[2]),
						y: Number(directionLineCoordinatesArray[3])
					};

					directionalLine.start = getScaledCoordinate(directionLineStart, imageDimensions, canvasDimensions, true);
					directionalLine.end = getScaledCoordinate(directionLineEnd, imageDimensions, canvasDimensions, true);
				}

				lineItem.start = getScaledCoordinate(start, imageDimensions, canvasDimensions, true);
				lineItem.end = getScaledCoordinate(end, imageDimensions, canvasDimensions, true);

				lineItem.directionLineCoordinates = getCrossingLineDirectionalArrow(lineItem.start, lineItem.end);

				crossingLinesArray.push(lineItem);
			}
		}
	}

	return {
		crossingLinesArray,
		directionalLine
	};
};
